import { createContext, ReactNode, useContext, useEffect } from 'react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';

import { SetupCache } from './setup-cache';
import { useSetupCache } from './hooks/use-setup-cache';
import type { SetupCacheControls } from './hooks/setup-cache-controls';
import { getSyncJob } from './sync-job/sync-job-store';
import { reseedPlotsListFromSyncJob } from './sync-job/run-sync-job';
import { SyncJobProvider } from './sync-job/sync-job-provider';

const SetupCacheControlsContext = createContext<SetupCacheControls | null>(
  null
);

function OfflineSyncRootInner({ children }: { children: ReactNode }) {
  const controls = useSetupCache();
  const queryClient = useQueryClient();

  useEffect(() => {
    const tryReseed = async () => {
      if (onlineManager.isOnline()) {
        return;
      }
      const job = await getSyncJob();
      if (job && job.totals.plots > 0) {
        reseedPlotsListFromSyncJob(queryClient, job);
      }
    };
    tryReseed();
    return onlineManager.subscribe((online) => {
      if (!online) {
        tryReseed();
      }
    });
  }, [queryClient]);

  return (
    <SetupCacheControlsContext.Provider value={controls}>
      <SetupCache controls={controls} />
      {children}
    </SetupCacheControlsContext.Provider>
  );
}

/** Wraps the app: single sync job context + setup-cache controls for Ionic overlays. */
export function OfflineSyncRoot({ children }: { children: ReactNode }) {
  return (
    <SyncJobProvider>
      <OfflineSyncRootInner>{children}</OfflineSyncRootInner>
    </SyncJobProvider>
  );
}

export function useSetupCacheControls(): SetupCacheControls {
  const ctx = useContext(SetupCacheControlsContext);
  if (!ctx) {
    throw new Error(
      'useSetupCacheControls must be used within OfflineSyncRoot'
    );
  }
  return ctx;
}
